<template>
  <div class="page-wrapper">
    <!-- Theme information -->
    <ThemeBanner :currentLearningType="currentLearningType" :currentTheme="currentTheme" />

    <div class="module-list-box hub-grey">
      <!-- Top Filter -->
      <div class="module-filter">
        <Selector :optionList="topicList" :selectedKey="topicId" code="id" label="name" :onChange="updateModuleList" />
      </div>

      <!-- Card List -->
      <ul class="module-list">
        <li class="module-card" v-for="topicModule in moduleList" :key="topicModule.id">
          <div class="preview">
            <img
              :src="$path.resolveImage(topicModule.image, $assets.image.default['default-blank-theme.svg'])"
              alt=""
            />

            <!-- Time estimate -->
            <div class="time-estimate">
              <div class="svg-icon">
                <inline-svg :src="$assets.svg['icon-time-estimate']" width="100%" height="100%" />
              </div>
              <p>{{ topicModule.estimate }} Min</p>
            </div>

            <!-- Overlay -->
            <div class="overlay">
              <button type="button" class="enter-btn clickable" @click="launchModule(topicModule)">Enter Module</button>
            </div>
          </div>

          <div class="info">
            <p class="title">{{ topicModule.shortName }}</p>
            <p class="desc">
              <span v-html="topicModule.shortSummary" />
            </p>

            <!-- Module card bottom bar -->
            <div class="footer">
              <button
                type="button"
                :class="{
                  'journal-button': true,
                  'hub-link': true,
                  disabled: !topicModule.journals,
                }"
                :disabled="!topicModule.journals"
                @click="showPDFList(topicModule, 'journals', topicModule.shortName)"
              >
                <div class="svg-icon">
                  <inline-svg :src="$assets.svg['journal_icon']" width="100%" height="100%" />
                </div>
                Journal
              </button>
              <div class="v-line" />
              <button
                type="button"
                :class="{
                  'journal-button': true,
                  'hub-link': true,
                  disabled: !topicModule.guides,
                }"
                :disabled="!topicModule.guides"
                @click="showPDFList(topicModule, 'guides', topicModule.shortName)"
              >
                <div class="svg-icon">
                  <inline-svg :src="$assets.svg['guide_icon']" width="100%" height="100%" />
                </div>
                Guide
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Info: Select language for pdf file -->
    <div class="pdf-links-box" :class="{ showed: pdfPopupShow }">
      <div class="text">
        <p class="title">{{ pdfModuleName }} - {{ pdfPopupTitle }}</p>
        <p class="subtitle">Please select the appropriate language guide below.</p>
      </div>

      <div class="buttons">
        <div v-for="(pdf, index) in modulePdfs" :key="index">
          <div
            @click="openPdf(pdf.src)"
            @keypress.enter="openPdf(pdf.src)"
            target="_blank"
            class="pdf-button"
            :class="{ active: pdf.src }"
          >
            {{ pdf.language }}
          </div>
        </div>
      </div>

      <p class="right-top-btn hub-close-normal clickable" @click="closePDFList" @keypress.enter="closePDFList">
        <inline-svg :src="$assets.svg['icon-close']" width="100%" height="100%" />
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import $assets from '@/assets';
import Selector from '@/components/common/DropdownSelector.vue';
import ThemeBanner from '@/components/common/ThemeBanner.vue';
import ModuleService from '@/services/module';
import TopicService from '@/services/topic';
import useSessionStore from '@/stores/session';
import $path from '@/utils/path';

const props = defineProps({
  topicId: {
    type: String,
    required: true,
  },
});

const $router = useRouter();
const SessionStore = useSessionStore();
const topicList = ref([]);
const moduleList = ref([]);
const modulePdfs = ref([]);
const pdfPopupShow = ref(false);
const pdfPopupTitle = ref(null);
const pdfModuleName = ref(null);

const currentLearningType = computed(() => {
  return SessionStore.HUBSettings.currentLearningType;
});

const currentTheme = computed(() => {
  return SessionStore.HUBSettings.currentTheme;
});

onBeforeMount(async () => {
  const data = await TopicService.getTopics(currentTheme.value.id);
  topicList.value = data.records;
});

function parseDocMap(docMap) {
  if (!docMap) return null;

  const parsedMap = JSON.parse(docMap);
  const docs = Object.keys(parsedMap).map((l) => ({
    language: l,
    src: $path.resolveFile(parsedMap[l]),
  }));

  return docs.length > 0 ? docs : null;
}

async function getModuleList() {
  const moduleResults = await ModuleService.getModules(props.topicId);
  moduleList.value = moduleResults.records.map((md) => ({
    ...md,
    // shortSummary: md.summary.substr(0, 104) + (md.summary.length > 104 ? ' ...' : ''),
    // shortName: md.name.substr(0, 22) + (md.name.length > 2 ? ' ...' : ''),
    shortSummary: md.summary,
    shortName: md.name,
    guides: parseDocMap(md.guides),
    journals: parseDocMap(md.journals),
  }));
}

function openPdf(url) {
  if (url) window.open(url, '_blank');
}

function showPDFList(topicModule, type, moduleName) {
  if (!topicModule[type]) return;
  if (topicModule[type].length === 1) {
    openPdf(topicModule[type][0].src);
    return;
  }

  pdfPopupTitle.value = type === 'guides' ? 'Facilitator Guide' : 'Journal';
  pdfModuleName.value = moduleName;
  modulePdfs.value = topicModule[type];
  pdfPopupShow.value = true;

  // $el.ownerDocument.body.classList.add('lock');
}

function closePDFList() {
  pdfPopupShow.value = false;
  // $el.ownerDocument.body.classList.remove('lock');
}

function updateModuleList(topic) {
  $router.push({
    name: 'moduleList',
    params: { topicId: topic.id },
  });
}

function launchModule(topicModule) {
  $router.push({
    name: 'moduleLaunch',
    params: { topicId: props.topicId, moduleId: topicModule.id },
  });
}

watch(
  () => props.topicId,
  () => {
    getModuleList();
  },
  { immediate: true }
);
</script>

<style scoped>
.pdf-links-box {
  position: fixed;
  left: 2%;
  bottom: -1em;
  transform: translateY(100%);

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  border: 0 none;
  border-radius: 0.4em 0.4em 0 0;
  box-shadow: 0 0 0.4em 0 #a7a9ac;
  width: 96%;
  min-height: 50%;
  padding: 1.2em 0.6em 2.4em;
  background: #fff;

  transition: transform 0.2s;
}
.pdf-links-box.showed {
  transform: translateY(0);
}

.pdf-links-box .right-top-btn {
  position: absolute;
  right: 0.8em;
  top: 0.4em;
  width: 2.4em;
  height: 2.4em;
}

.pdf-links-box .text {
  margin: 1em;
}
.text .title {
  font-size: 1.4em;
  color: #8ac640;
  text-align: center;
}
.text .subtitle {
  margin-top: 3%;
  font-size: 1.1em;
  color: #808285;
  text-align: center;
}

.pdf-links-box .buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(4em, 6em));
  column-gap: 2em;
  justify-content: center;
  align-items: center;
  margin: 1em;
}
.buttons .pdf-button {
  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 0.4em;
  box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.3);
  min-width: 4em;
  padding: 1em 0.4em;

  background: #fff;
  color: #808285;
  cursor: pointer;
}
.pdf-button.active:hover {
  background: #8ac640;
  color: #fff;
}

@media screen and (min-width: 767px) and (min-height: 391px) {
  .pdf-links-box {
    position: fixed;
    transition: initial;
  }

  .pdf-links-box.showed {
    left: 50%;
    bottom: initial;
    top: 50%;
    transform: translate(-50%, -50%);

    border-radius: 0.4em;
    width: 60%;
    min-width: 30em;
    max-width: 40em;
    height: 30%;
    min-height: 20em;
    max-height: 25em;
    padding: 1.8em;
  }

  .pdf-links-box .right-top-btn {
    right: 1.2em;
    top: 0.8em;
    width: 2.6em;
    height: 2.6em;
  }

  .pdf-links-box .buttons {
    column-gap: 4em;
  }

  .buttons .pdf-button {
    padding: 1.4em 0.4em;
  }
}
</style>
